import React, { useMemo, useState } from "react";
import { OGDB2_METRIC_CATEGORIES } from "../metricRegistry";

/**
 * BulkAnalysisColumnsModal (per-column selection)
 *
 * columnConfig shape:
 * {
 *   identityKeys: string[],
 *   flagsOn: boolean,
 *   flagKeys: string[],
 *
 *   // ✅ explicit column keys (fully independent columns)
 *   // examples:
 *   // - m.lease_number.assessor
 *   // - m.lease_number.ba
 *   // - d.loe_comparable.ba_abs
 *   metricColumnKeys: string[],
 *
 *   showDeltas: boolean,
 *   deltaOverrides: { [metricCode]: boolean }
 * }
 */

function pillClass(on) {
  return on
    ? "px-3 py-1 rounded-full text-xs font-semibold bg-gray-900 text-white"
    : "px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700";
}

function normalize(s) {
  return String(s || "").toLowerCase().trim();
}

// Default: deltas only for negotiable metrics (prod/exp/price/valuation).
const OBJECTIVE_CODE_BLOCKLIST = new Set([
  "lease_name",
  "lease_number",
  "rrc_number",
  "rrc",
  "well_name",
  "display_name",
  "client",
  "county",
  "operator",
  "interest_type",
  "interest_percent",
  "external_account_id",
  "account_id",
  "appraisal_firm",
]);

function isNegotiableMetric(metricDef) {
  const code = String(metricDef?.code || "");
  if (!code) return false;

  if (OBJECTIVE_CODE_BLOCKLIST.has(code)) return false;

  const cats = metricDef?.categories || [];
  const hasNegotiableCategory =
    cats.includes(OGDB2_METRIC_CATEGORIES.PRODUCTION) ||
    cats.includes(OGDB2_METRIC_CATEGORIES.EXPENSES) ||
    cats.includes(OGDB2_METRIC_CATEGORIES.PRICE_DECK) ||
    cats.includes(OGDB2_METRIC_CATEGORIES.VALUATION);

  if (hasNegotiableCategory) return true;

  const label = normalize(metricDef?.label || "");
  if (
    label.includes("lease") ||
    label.includes("rrc") ||
    label.includes("account") ||
    label.includes("operator") ||
    label.includes("county") ||
    label.includes("client") ||
    label.includes("interest")
  ) {
    return false;
  }

  return false;
}

/**
 * Buckets match BAIDDetailPage's registry bucket assignment rules:
 * preferred display category (first match wins):
 * production -> expenses -> price_deck -> ownership -> valuation -> facts_quick -> other
 */
const BUCKET_KEYS = {
  FACTS_QUICK: "facts_quick",
  OWNERSHIP: "ownership",
  PRODUCTION: "production",
  PRICE_DECK: "price_deck",
  EXPENSES: "expenses",
  VALUATION: "valuation",
  OTHER: "other",
};

const BUCKET_ORDER = [
  BUCKET_KEYS.FACTS_QUICK,
  BUCKET_KEYS.OWNERSHIP,
  BUCKET_KEYS.PRODUCTION,
  BUCKET_KEYS.PRICE_DECK,
  BUCKET_KEYS.EXPENSES,
  BUCKET_KEYS.VALUATION,
  BUCKET_KEYS.OTHER,
];

function bucketTitle(key) {
  if (key === BUCKET_KEYS.FACTS_QUICK) return "Quick Facts";
  if (key === BUCKET_KEYS.OWNERSHIP) return "Ownership (Key Splits)";
  if (key === BUCKET_KEYS.PRODUCTION) return "Production (Forecast Drivers)";
  if (key === BUCKET_KEYS.PRICE_DECK) return "Price Deck";
  if (key === BUCKET_KEYS.EXPENSES) return "Expenses";
  if (key === BUCKET_KEYS.VALUATION) return "Valuation Assumptions";
  return "Other";
}

function preferredBucket(metricDef) {
  const c = metricDef?.categories || [];

  // exactly mirrors BAIDDetailPage's bucket build order
  if (c.includes(OGDB2_METRIC_CATEGORIES.PRODUCTION)) return BUCKET_KEYS.PRODUCTION;
  if (c.includes(OGDB2_METRIC_CATEGORIES.EXPENSES)) return BUCKET_KEYS.EXPENSES;
  if (c.includes(OGDB2_METRIC_CATEGORIES.PRICE_DECK)) return BUCKET_KEYS.PRICE_DECK;
  if (c.includes(OGDB2_METRIC_CATEGORIES.OWNERSHIP)) return BUCKET_KEYS.OWNERSHIP;
  if (c.includes(OGDB2_METRIC_CATEGORIES.VALUATION)) return BUCKET_KEYS.VALUATION;
  if (c.includes(OGDB2_METRIC_CATEGORIES.FACTS_QUICK)) return BUCKET_KEYS.FACTS_QUICK;

  return BUCKET_KEYS.OTHER;
}

function keyM(code, scenario) {
  return `m.${code}.${scenario}`;
}
function keyD(code, kind) {
  return `d.${code}.${kind}`;
}

export default function BulkAnalysisColumnsModal({
  open,
  onClose,
  registryMetricsUnique,
  registryMetricsByGroup, // kept for compatibility; not used for grouping anymore
  identityColumns,
  value,
  onChange,
}) {
  const [tab, setTab] = useState("metrics"); // identity | flags | metrics
  const [search, setSearch] = useState("");

  const config = value || {};
  const identityKeys = Array.isArray(config.identityKeys) ? config.identityKeys : [];

  // ✅ explicit keys
  const metricColumnKeys = new Set(Array.isArray(config.metricColumnKeys) ? config.metricColumnKeys : []);

  // keep old settings around (BulkAnalysisPage may still use them for migration)
  const showDeltas = config.showDeltas !== false;
  const deltaOverrides = config.deltaOverrides || {};

  const metricIndex = useMemo(() => {
    const map = new Map();
    for (const m of registryMetricsUnique || []) {
      if (m?.code) map.set(m.code, m);
    }
    return map;
  }, [registryMetricsUnique]);

  const searchNeedle = normalize(search);

  const derivedGroups = useMemo(() => {
    const groups = {
      [BUCKET_KEYS.FACTS_QUICK]: [],
      [BUCKET_KEYS.OWNERSHIP]: [],
      [BUCKET_KEYS.PRODUCTION]: [],
      [BUCKET_KEYS.PRICE_DECK]: [],
      [BUCKET_KEYS.EXPENSES]: [],
      [BUCKET_KEYS.VALUATION]: [],
      [BUCKET_KEYS.OTHER]: [],
    };

    for (const m of registryMetricsUnique || []) {
      if (!m?.code) continue;
      const b = preferredBucket(m);
      (groups[b] || (groups[b] = [])).push(m);
    }

    return groups;
  }, [registryMetricsUnique]);

  const filteredGroups = useMemo(() => {
    const out = {};
    for (const gk of BUCKET_ORDER) {
      const list = Array.isArray(derivedGroups?.[gk]) ? derivedGroups[gk] : [];

      if (!searchNeedle) {
        out[gk] = list;
        continue;
      }

      out[gk] = list.filter((m) => {
        const code = normalize(m?.code);
        const label = normalize(m?.label);
        return code.includes(searchNeedle) || label.includes(searchNeedle);
      });
    }
    return out;
  }, [derivedGroups, searchNeedle]);

  // Hide empty buckets (clean UI) unless searching and bucket has matches
  const visibleBucketOrder = useMemo(() => {
    return BUCKET_ORDER.filter((gk) => {
      const total = (derivedGroups?.[gk] || []).length;
      const shown = (filteredGroups?.[gk] || []).length;
      if (searchNeedle) return shown > 0;
      return total > 0;
    });
  }, [derivedGroups, filteredGroups, searchNeedle]);

  function update(next) {
    onChange?.(next);
  }

  function toggleIdentity(key) {
    const set = new Set(identityKeys);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    update({ ...config, identityKeys: Array.from(set) });
  }

  function setAllIdentity(on) {
    update({ ...config, identityKeys: on ? identityColumns.map((c) => c.key) : [] });
  }

  function deltaAllowedFor(code) {
    const def = metricIndex.get(code);
    if (Object.prototype.hasOwnProperty.call(deltaOverrides, code)) return !!deltaOverrides[code];
    return isNegotiableMetric(def);
  }

  function toggleMetricScenario(code, scenario) {
    const k = keyM(code, scenario);
    const set = new Set(metricColumnKeys);
    if (set.has(k)) set.delete(k);
    else set.add(k);

    update({
      ...config,
      metricColumnKeys: Array.from(set),
    });
  }

  function toggleDeltaColumn(code, kind) {
    const k = keyD(code, kind);
    const set = new Set(metricColumnKeys);
    if (set.has(k)) set.delete(k);
    else set.add(k);

    update({
      ...config,
      metricColumnKeys: Array.from(set),
    });
  }

  function setGroupAll(gk, on) {
    const arr = (derivedGroups?.[gk] || []).filter((m) => m?.code);
    const set = new Set(metricColumnKeys);

    for (const m of arr) {
      const code = m.code;

      // scenarios
      for (const sc of ["assessor", "actual", "third_party", "ba"]) {
        const k = keyM(code, sc);
        if (on) set.add(k);
        else set.delete(k);
      }

      // deltas (only if enabled and allowed)
      if (showDeltas && deltaAllowedFor(code)) {
        for (const kind of ["act_abs", "act_pct", "ba_abs", "ba_pct"]) {
          const dk = keyD(code, kind);
          if (on) set.add(dk);
          else set.delete(dk);
        }
      }
    }

    update({
      ...config,
      metricColumnKeys: Array.from(set),
    });
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      <div className="relative w-[min(1100px,95vw)] max-h-[85vh] overflow-hidden rounded-2xl bg-white border border-gray-200 shadow-xl">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-gray-900">Columns</div>
            <div className="text-xs text-gray-500">
              Columns are independent per metric (Assessor / Actual / 3rd Party / BA) + optional deltas.
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button className={pillClass(tab === "identity")} onClick={() => setTab("identity")} type="button">
              Identity
            </button>
            <button className={pillClass(tab === "metrics")} onClick={() => setTab("metrics")} type="button">
              Metrics
            </button>

            <button className="btn" onClick={onClose} type="button">
              Close
            </button>
          </div>
        </div>

        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!showDeltas}
              onChange={(e) => update({ ...config, showDeltas: !!e.target.checked })}
            />
            Enable delta columns
          </label>

          {tab === "metrics" ? (
            <div className="ml-auto w-full md:w-[420px]">
              <input
                className="w-full rounded-xl border border-gray-200 px-3 py-2"
                placeholder="Search metrics by name or code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          ) : null}
        </div>

        <div className="p-4 overflow-auto max-h-[65vh]">
          {tab === "identity" ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <button className="btn" type="button" onClick={() => setAllIdentity(true)}>
                  Select all
                </button>
                <button className="btn" type="button" onClick={() => setAllIdentity(false)}>
                  Clear
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {(identityColumns || []).map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm text-gray-800">
                    <input type="checkbox" checked={identityKeys.includes(c.key)} onChange={() => toggleIdentity(c.key)} />
                    <span className="font-medium">{c.label}</span>
                    <span className="text-xs text-gray-500">{c.key}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {tab === "metrics" ? (
            <div className="space-y-6">
              {visibleBucketOrder.map((gk) => {
                const list = filteredGroups?.[gk] || [];
                const total = (derivedGroups?.[gk] || []).length || 0;
                const shown = list.length;

                return (
                  <div key={gk} className="rounded-2xl border border-gray-200 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <div className="text-sm font-semibold text-gray-800">
                        {bucketTitle(gk)}{" "}
                        <span className="text-xs text-gray-500">
                          ({shown}/{total}
                          {searchNeedle ? " matching" : ""})
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <button className="btn" type="button" onClick={() => setGroupAll(gk, true)}>
                          Select group
                        </button>
                        <button className="btn" type="button" onClick={() => setGroupAll(gk, false)}>
                          Clear group
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2">
                      {list.map((m) => {
                        const code = m?.code;
                        const label = m?.label || code;
                        const deltaOk = deltaAllowedFor(code);

                        const onA = metricColumnKeys.has(keyM(code, "assessor"));
                        const onAct = metricColumnKeys.has(keyM(code, "actual"));
                        const on3P = metricColumnKeys.has(keyM(code, "third_party"));
                        const onBA = metricColumnKeys.has(keyM(code, "ba"));

                        return (
                          <div key={code} className="rounded-xl border border-gray-100 p-3 flex flex-wrap items-center justify-between gap-3">
                            <div className="min-w-[240px]">
                              <div className="font-medium text-sm text-gray-900">{label}</div>
                              <div className="text-xs text-gray-500">{code}</div>
                            </div>

                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2 text-sm text-gray-800">
                                <input type="checkbox" checked={onA} onChange={() => toggleMetricScenario(code, "assessor")} />
                                A
                              </label>
                              <label className="flex items-center gap-2 text-sm text-gray-800">
                                <input type="checkbox" checked={onAct} onChange={() => toggleMetricScenario(code, "actual")} />
                                Act
                              </label>
                              <label className="flex items-center gap-2 text-sm text-gray-800">
                                <input type="checkbox" checked={on3P} onChange={() => toggleMetricScenario(code, "third_party")} />
                                3P
                              </label>
                              <label className="flex items-center gap-2 text-sm text-gray-800">
                                <input type="checkbox" checked={onBA} onChange={() => toggleMetricScenario(code, "ba")} />
                                BA
                              </label>

                              <div className="h-6 w-px bg-gray-200" />

                              <label className="flex items-center gap-2 text-xs text-gray-700" title={!showDeltas ? "Enable deltas at top" : undefined}>
                                <input
                                  type="checkbox"
                                  disabled={!showDeltas || !deltaOk}
                                  checked={metricColumnKeys.has(keyD(code, "act_abs"))}
                                  onChange={() => toggleDeltaColumn(code, "act_abs")}
                                />
                                ΔAct
                              </label>
                              <label className="flex items-center gap-2 text-xs text-gray-700">
                                <input
                                  type="checkbox"
                                  disabled={!showDeltas || !deltaOk}
                                  checked={metricColumnKeys.has(keyD(code, "act_pct"))}
                                  onChange={() => toggleDeltaColumn(code, "act_pct")}
                                />
                                %ΔAct
                              </label>
                              <label className="flex items-center gap-2 text-xs text-gray-700">
                                <input
                                  type="checkbox"
                                  disabled={!showDeltas || !deltaOk}
                                  checked={metricColumnKeys.has(keyD(code, "ba_abs"))}
                                  onChange={() => toggleDeltaColumn(code, "ba_abs")}
                                />
                                ΔBA
                              </label>
                              <label className="flex items-center gap-2 text-xs text-gray-700">
                                <input
                                  type="checkbox"
                                  disabled={!showDeltas || !deltaOk}
                                  checked={metricColumnKeys.has(keyD(code, "ba_pct"))}
                                  onChange={() => toggleDeltaColumn(code, "ba_pct")}
                                />
                                %ΔBA
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {visibleBucketOrder.length === 0 ? (
                <div className="text-sm text-gray-500">No metrics match your search.</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
